import { useRoute } from 'vue-router';
import axios from 'axios';
import { router } from '@/routes/router';
import { useApplicationStore } from '@/stores/applicationStore.js';
import { findStepByRoute } from '@/helpers/steps.js';
import { redirectRecognizedUser } from '@/helpers/recognizedEmailHelper.js';

axios.defaults.baseURL = window.BASE;

/**
 *  Function to log messages
 *
 * @param {*} message
 */
export function log(message) {
    console.log(message);
}

/**
 *  Function to log (warnings)
 *
 * @param {*} message
 */
export function warn(message) {
    console.warn(message);
}

/**
 * Function to validate a route
 *
 * @param {*} route
 * @returns
 */
export function isValidRoute(routePath) {
    return routePath && routePath.trim().length;
}

/**
 * Function to redirect to a page
 *
 * @param {*} page
 */
export function goTo(path) {
    router.push(path);
}

/**
 * Function to redirect to a page
 *
 * @param {*} form
 * @returns
 */
export function setInvalidForm(form) {
    if (!form) return;
    form.classList.add('invalid-form');
}

/**
 * Function to set a form as valid
 *
 * @param {*} form
 * @returns
 */
export function setValidForm(form) {
    if (!form) return;
    form.classList.remove('invalid-form');
}

/**
 * Function to add an error message to a context
 *
 * @param {*} context
 * @param {*} message
 * @returns
 */
export function addContextErrorMessage(context, message) {
    if (!context) return;
    context.errorMessage = { message: message };
}

export function clearContextErrorMessage(context) {
    if (!context) return;
    context.errorMessage = { message: '' };
}

/**
 * Function to validate required field
 *
 * @param {*} field
 * @returns
 */
export function validateRequiredField(field, context) {
    if (!field) return true;

    const form = field.closest('form');
    const fieldType = field.classList.contains('dropdown-form-field')
        ? 'dropdown-form-field'
        : field.type;
    let message = field.hasAttribute('data-number-field')
        ? 'Please fill in amount in order to proceed.'
        : 'This field is required.'; // Default error message

    switch (fieldType) {
        case 'select-one':
        case 'select-multiple':
        case 'dropdown-form-field':
            message = 'Please select an option';
            break;
        case 'checkbox':
            message = 'Please check form fields.';
            break;
        case 'radio':
            message = 'Please select an option';
            break;
        default:
        // Do nothing
    }

    let isValid = true;

    // Check if the field is a radio button
    if (field.type === 'radio') {
        const nameAttribute = field.getAttribute('name');
        const fieldGroup = document.querySelectorAll(`input[name="${nameAttribute}"]`);
        const fieldGroupHasrequired = document.querySelectorAll(
            `input[name="${nameAttribute}"]:required`
        );
        let anyChecked = Array.from(fieldGroup).some((input) => input.checked);

        if (!anyChecked && fieldGroupHasrequired.length) {
            addContextErrorMessage(context, message);
            setInvalidForm(form);
            isValid = false;
        } else {
            clearContextErrorMessage(context);
            setValidForm(form);
        }
    }
    // checkbox fields
    else if (field.type === 'checkbox') {
        const targetLabel = document.querySelector(`label[for="${field.id}"]`);

        if (field.required && !field.checked) {
            addContextErrorMessage(context, message);
            setInvalidForm(form);
            isValid = false;

            if (targetLabel) {
                targetLabel.classList.add('invalid');
            }
        } else {
            clearContextErrorMessage(context);
            if (targetLabel) {
                targetLabel.classList.remove('invalid');
            }
            // Remove invalid-form class if no other validation errors exist
            if (form && !form.querySelectorAll('.validation-error').length) {
                setValidForm(form);
            }
        }
    } else if (field.type === 'select-one') {
        if (field.required && (field.value === '' || field.value === '-1' || field.value === '0')) {
            addFieldError(field, message);
            isValid = false;
        } else {
            removeFieldError(field);
        }
    } else if (field.classList.contains('dropdown-form-field')) {
        if (field.getAttribute('required') && !field.getAttribute('data-value').trim().length) {
            addFieldError(field, message);
            isValid = false;
        } else {
            removeFieldError(field);
        }
    }

    // Validate other fields
    else {
        if (field.required && !field.value.trim()) {
            addFieldError(field, message);
            isValid = false;
        } else {
            removeFieldError(field);
        }
    }

    return isValid;
}

/**
 * Function to add a next route to a context
 *
 * @param {*} context
 * @param {*} route
 * @returns
 */
export function addNextRoute(context, route) {
    if (!context || !route) return;

    // Check if we're dealing with a Vue instance and nextRoute is a computed property
    if (context.$options && context.$options.computed && context.$options.computed.nextRoute) {
        // Skip setting computed properties without setters - they should handle their own logic
        return;
    }

    context.nextRoute = route;
}

/**
 * Function to update the next route
 *
 * @returns
 */
export function setNextRoute(context, event) {
    if (!context || !event) return;

    // Access the data-route attribute of the selected radio button
    const route = event.target && event.target.dataset.route ? event.target.dataset.route : '';

    if (route.length) {
        context.nextRoute = route;
    }
}

export function hideFormGlobalError(context) {
    if (!context) return;
    context.errorMessage.message = '';
}

export function validatePQForm(context) {
    const form =
        document.querySelector('.pq-rework-form') ||
        document.querySelector('[data-pq-rework-form]');
    if (!form) return true;

    // set default to valid form
    let isValidForm = true;

    if (form) {
        const formFields = form.querySelectorAll('input, textarea, select');
        const textFields = ['text', 'textarea', 'email', 'number', 'search', 'password'];
        const checkBoxesAndRadioFields = ['checkbox', 'radio'];
        const customDropdowns = form.querySelectorAll('.dropdown-form-field');

        if (formFields.length) {
            for (const field of formFields) {
                const fieldType = field.type;

                if (field.required && !field.value && textFields.includes(fieldType)) {
                    if (!validateRequiredField(field)) {
                        isValidForm = false;
                    }
                } else if (checkBoxesAndRadioFields.includes(fieldType)) {
                    if (!validateRequiredField(field, context)) {
                        isValidForm = false;
                    }
                } else if (fieldType === 'email' || field.hasAttribute('data-email-field')) {
                    if (!validateEmailField(field)) {
                        isValidForm = false;
                    }
                } else if (fieldType === 'number' || field.hasAttribute('data-number-field')) {
                    if (!validateNumberField(field)) {
                        isValidForm = false;
                    }
                } else if (fieldType === 'select-one' || fieldType === 'select-multiple') {
                    if (!validateRequiredField(field)) {
                        isValidForm = false;
                    }
                }
            }
        }

        if (customDropdowns.length) {
            for (const dropdown of customDropdowns) {
                const dropdownValue = dropdown.getAttribute('data-value') || '';
                const dropdownRequired = dropdown.getAttribute('required');

                if (dropdownRequired === 'true' && !dropdownValue.trim().length) {
                    if (!validateRequiredField(dropdown)) {
                        isValidForm = false;
                    }
                }
            }
        }
    }

    return isValidForm;
}

/**
 *  Function to validate a PQ form
 *
 * @param {*} form
 * @returns
 */
export function isValidPQForm(form) {
    form =
        form ||
        document.querySelector('.pq-rework-form') ||
        document.querySelector('[data-pq-rework-form]');
    if (!form) return true;

    return form.querySelectorAll('.validation-error').length ||
        form.classList.contains('invalid-form')
        ? false
        : true;
}

/**
 * Handle form submission
 *
 * @param {*} context
 * @param {*} event
 * @returns boolean
 */
export function submitHandler(context, event) {
    console.warn('Form direct submission not allowed. Please use [Continue] button.');
    return false;
}

export let errorTimeout = null;

/**
 * Continue button handler
 *
 * @returns boolean
 */
export function continueHandler(context, event) {
    const nextRoute = context.nextRoute.length ? context.nextRoute : '';
    let returnValue = {
        hasError: false,
        nextRoute: nextRoute
    };

    // first validate the form, then look for errors
    validatePQForm(context);

    // now check if form has any errors
    const isValidForm = isValidPQForm();

    if (isValidForm) {
        if (isValidRoute(nextRoute)) {
            goTo(nextRoute);
        } else {
            warn('Route is not valid: ' + nextRoute);
        }
    } else {
        context.errorMessage = { message: 'Please check the form and try again.' };
        returnValue.hasError = true;

        // Clear previous timeout
        if (errorTimeout) {
            clearTimeout(errorTimeout);
        }

        errorTimeout = setTimeout(() => {
            hideFormGlobalError(context);
            errorTimeout = null;
        }, 3000);
    }

    return returnValue;
}

export function hasEmptyRequiredFields(fields) {
    if (!fields) return false;
    return Object.values(fields).some((field) => field.required && !field.value);
}

/**
 * Function to format large numbers with commas
 *
 * @param {*} value
 * @returns
 */
export function formatLargeNumbers(value) {
    if (!value) return '';
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Function to get the selected class form pq form options
 *
 * @param {*} selectedOption
 * @param {*} value
 * @returns
 */
export function getSelectedClass(selectedOption, value) {
    return selectedOption === value ? 'selected' : '';
}

export const isValidEmail = (value) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value);
};

export const isValidNumber = (value) => {
    const pricePattern = /^(?:\d{1,3}(?:,\d{3})*)(?:\.\d{1,2})?$/;
    return pricePattern.test(value);
};

export const isValidPhone = (value) => {
    // Remove all non-digits to check the core phone number
    const digitsOnly = value.replace(/\D/g, '');
    // US phone number should have exactly 10 digits
    return digitsOnly.length === 10 && /^\d{10}$/.test(digitsOnly);
};

export const validateEmailField = (field, message) => {
    if (!field) return true;
    message = message || 'Please enter a valid email address.';
    const value = field.value;
    let isValid = true;

    if (field && value.length) {
        if (!isValidEmail(value)) {
            addFieldError(field, message);
            isValid = false;
        } else {
            removeFieldError(field);
        }
    }

    return isValid;
};

export const validateNumberField = (field, message) => {
    if (!field) return true;
    message = message || 'Invalid number.';
    const value = field.value;
    let isValid = true;

    if (field && value.length) {
        if (!isValidNumber(value)) {
            addFieldError(field, message);
            isValid = false;
        } else {
            removeFieldError(field);
        }
    }

    return isValid;
};

export const validatePhoneField = (field, message) => {
    if (!field) return true;
    message = message || 'Please enter a valid 10-digit phone number.';
    const value = field.value;
    let isValid = true;

    if (field && value.length) {
        if (!isValidPhone(value)) {
            addFieldError(field, message);
            isValid = false;
        } else {
            removeFieldError(field);
        }
    }

    return isValid;
};

export const validateName = (value) => {
    const namePattern = /^[a-zA-Z\s]+$/;
    return namePattern.test(value) && value.trim() !== '';
};

export function handleElelemtActiveClass(element) {
    if (!element) return;
    const elementWrapper = element.closest('.pq-form-field');

    if (elementWrapper) {
        if (element.type === 'select-one') {
            if (element.value !== '' && element.value !== '-1' && element.value !== '0') {
                elementWrapper.classList.add('active');
            } else {
                elementWrapper.classList.remove('active');
            }
        } else {
            const elementValue =
                element.value.toString().trim() ||
                (element.getAttribute('data-value')
                    ? element.getAttribute('data-value').toString().trim()
                    : '');

            if (elementValue.length) {
                elementWrapper.classList.add('active');
            } else {
                elementWrapper.classList.remove('active');
            }
        }
    }
}

/**
 * Insert a new element after a target element.
 *
 * @param {HTMLElement} targetElement - The element after which the new element should be inserted.
 * @param {HTMLElement} newElement - The new element to insert.
 */
export function insertAfter(targetElement, newElement) {
    if (targetElement && newElement) {
        targetElement.insertAdjacentElement('afterend', newElement);
    }
}

/**
 * Function to handle element active state
 * @param {*} event
 * @returns
 */
export function handleElelemtActiveState(event) {
    if (!event || !event.target) return;
    handleElelemtActiveClass(event.target);
}

/**
 * Removes the next sibling of a given target element in the DOM.
 *
 * @param {HTMLElement} targetElement - The element from which we want to find the next sibling and remove it.
 */
function removeNextSibling(targetElement) {
    if (targetElement && targetElement.nextSibling) {
        targetElement.nextSibling.remove();
    }
}

/**
 * Function to add field error
 *
 * @param {*} field
 * @param {*} message
 * @returns
 */
export function addFieldError(field, message) {
    if (!field) return;
    const form = field.closest('form');
    const fieldParent = field.closest('.pq-form-field') || field.closest('.pq-option');
    message = message || '';

    if (fieldParent) {
        // first remove previous error message if it exists
        removeFieldError(field);

        // add invalid form class
        fieldParent.classList.add('validation-error');

        // add form invalid form class
        if (form) {
            form.classList.add('invalid-form');
        }

        if (message.length) {
            const errorElement = document.createElement('div');
            errorElement.classList.add('error-info');
            errorElement.innerHTML = `
            <div class="flex gap-12px text-15px px-16px py-12px rounded-12px border-1px border-solid border-light-brown bg-faded-white my-12px">
                <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                        <path d="M10.0013 6.1665V9.49984M10.0013 12.8332H10.0096M18.3346 9.49984C18.3346 14.1022 14.6037 17.8332 10.0013 17.8332C5.39893 17.8332 1.66797 14.1022 1.66797 9.49984C1.66797 4.89746 5.39893 1.1665 10.0013 1.1665C14.6037 1.1665 18.3346 4.89746 18.3346 9.49984Z" stroke="#2D2D2D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                </div>
                <div>
                    ${message}
                </div>
            </div>`;

            insertAfter(fieldParent, errorElement);
        }
    }
}

/**
 * Function to remove field error
 *
 * @param {*} field
 * @returns
 */
export function removeFieldError(field) {
    if (!field) return;
    const form = field.closest('form');
    const fieldParent = field.closest('.pq-form-field');

    if (fieldParent) {
        fieldParent.classList.remove('validation-error');

        // remove error message if it exists
        if (
            fieldParent &&
            fieldParent.nextElementSibling &&
            fieldParent.nextElementSibling.classList.contains('error-info')
        ) {
            removeNextSibling(fieldParent);
        }
    }

    // remove invalid form class
    if (form && !form.querySelectorAll('.validation-error').length) {
        form.classList.remove('invalid-form');
    }
}

/**
 * Function to set the selected option on page load
 *
 */
export function setSelectedOption(context) {
    const optionInput = document.querySelectorAll('.pq-option-field'); // input[type="radio"] or input[type="checkbox"]

    if (optionInput.length) {
        optionInput.forEach((input) => {
            let shouldBeActive = false;

            // Get current route to determine if we're on the home-buying index page or home-buy-sell journey page
            const currentRoute = useRoute();
            const isHomeBuyingIndex = currentRoute?.path === '/home-buying';
            const isHomeBuySellJourney =
                currentRoute?.path === '/home-buy-sell/where-home-buying-journey';

            // Getting-started page values that should map to the first option on index page
            const gettingStartedValues = ['in30Days', '0to3Months', '3to6Months', 'NotSure'];

            // Special handling for home-buying index page and home-buy-sell journey page
            if (
                (isHomeBuyingIndex || isHomeBuySellJourney) &&
                input.value === 'in30Days' &&
                input.hasAttribute('data-current') &&
                gettingStartedValues.includes(input.dataset.current)
            ) {
                shouldBeActive = true;
                input.checked = true; // Actually check the radio button
            }
            // Normal matching logic
            else if (input.hasAttribute('data-current') && input.value === input.dataset.current) {
                shouldBeActive = true;
            }

            if (shouldBeActive) {
                input.closest('.pq-option').classList.add('active');

                // add next route on page load depending on the active radio input
                if (context && input.checked && input.dataset.route) {
                    addNextRoute(context, input.dataset.route);
                }
            } else {
                input.closest('.pq-option').classList.remove('active');
            }
        });
    }
}

export function handleAddClass() {
    const addButtons = document.querySelectorAll('[data-add-class]');

    addButtons.forEach((button) => {
        button.addEventListener('change', (event) => {
            const className = button.getAttribute('data-add-class');
            const targetSelector = button.dataset.target;
            const targetElement = targetSelector ? document.querySelector(targetSelector) : null;

            if (targetElement) {
                targetElement.classList.add(className);
            }
        });
    });
}

export function handleRemoveClass() {
    const removeButtons = document.querySelectorAll('[data-remove-class]');

    removeButtons.forEach((button) => {
        button.addEventListener('change', (event) => {
            const className = button.getAttribute('data-remove-class');
            const targetSelector = button.dataset.target;
            const targetElement = targetSelector ? document.querySelector(targetSelector) : null;

            if (targetElement) {
                targetElement.classList.remove(className);
            }
        });
    });
}

export function handleToggleClass() {
    const toggleButtons = document.querySelectorAll('[data-toggle-class]');

    toggleButtons.forEach((button) => {
        button.addEventListener('change', (event) => {
            const className = button.getAttribute('data-toggle-class') || 'disabled';
            const isChecked = event.target.checked;
            const targetSelector = button.dataset.target;
            const targetElement = targetSelector ? document.querySelector(targetSelector) : null;

            if (targetElement) {
                if (isChecked) {
                    targetElement.classList.add(className);
                    // Clear validation error when field becomes disabled
                    if (className === 'disabled') {
                        const inputField = targetElement.querySelector('input, textarea, select');
                        if (inputField) {
                            removeFieldError(inputField);
                        }
                    }
                } else {
                    targetElement.classList.remove(className);
                }
            }
        });
    });
}

/**
 * Function to handle selected option change
 *
 */
export function handleSelectedOption(context) {
    // handle active class on page load
    setSelectedOption(context);

    const radioButtons = document.querySelectorAll('.pq-option-field'); // input[type="radio"] | it can be checkbox

    radioButtons.forEach((option) => {
        option.addEventListener('change', (event) => {
            const optionWrapper = event.target.closest('.pq-option');
            const optionType = option.type;

            if (optionWrapper) {
                const options = optionWrapper.parentElement.querySelectorAll('.pq-option');

                // remove active class from other options if is radio input
                if (optionType === 'radio') {
                    options.forEach((option) => {
                        option.classList.remove('active');
                    });
                }

                if (event.target.checked) {
                    optionWrapper.classList.add('active');
                } else {
                    optionWrapper.classList.remove('active');
                }
            }
        });
    });
}

/**
 * Function to handle next route update
 *
 * @param {*} event
 * @param {*} context
 * @returns
 */
export function handleNextRouteUpdate(event, context) {
    if (!context || !event) return;
    setNextRoute(context, event);
}

/**
 * Function to handle checkbox field
 *
 * @param {*} field
 */
export function handleCheckboxField(field) {
    if (!field || !field.hasAttribute('required')) return;

    const form = field.closest('form');
    const targetLabel = document.querySelector(`label[for="${field.id}"]`);

    if (field.checked) {
        if (targetLabel) {
            targetLabel.classList.remove('invalid');
        }
        // Remove field error if it exists
        removeFieldError(field);
        // Remove invalid-form class if no other validation errors exist
        if (form && !form.querySelectorAll('.validation-error').length) {
            setValidForm(form);
        }
    } else {
        if (targetLabel) {
            targetLabel.classList.add('invalid');
        }
        if (form) {
            setInvalidForm(form);
        }
    }
}

/**
 * Function to handle custom dropdown validation
 *
 */
export function handleCustomDropdownValidation() {
    const onDropdownChange = function (event) {
        const dropdownElement = event.detail.target;
        // const selectedValue = event.detail.value;

        validateRequiredField(dropdownElement);
    };

    // Add an event listener for the custom 'dropdownChange' event
    window.addEventListener('dropdownChange', onDropdownChange);
}

/**
 * Function to initialize form fields
 *
 * @param {*} context
 */
export function initFormFields(context) {
    const form =
        document.querySelector('.pq-rework-form') ||
        document.querySelector('[data-pq-rework-form]');

    if (form) {
        const formFields = form.querySelectorAll(
            'input.pq-text-field-input, input[type="checkbox"], textarea, select'
        );

        // handle toggle class
        handleToggleClass();
        handleAddClass();
        handleRemoveClass();
        handleCustomDropdownValidation();

        if (formFields) {
            // handle selected ptions
            handleSelectedOption(context);

            formFields.forEach((field) => {
                // handle active class on page load
                if (field.type !== 'select-one') {
                    handleElelemtActiveClass(field);
                }

                if (field.classList.contains('pq-text-field-input')) {
                    field.setAttribute('autocomplete', 'off');
                }

                const elementParentIcon = field.closest('.pq-form-field')
                    ? field.closest('.pq-form-field').querySelector('.text-field-icon') ||
                      field.closest('.pq-form-field').querySelector('.custom-field-icon')
                    : null;

                // handle text field icon click, focus the field
                if (elementParentIcon) {
                    elementParentIcon.addEventListener('click', (event) => {
                        event.preventDefault();
                        event.stopPropagation();

                        if (field.type === 'select-one') {
                            // do nothing
                        } else {
                            setTimeout(() => {
                                field.focus();
                            }, 100);
                        }
                    });
                }

                // handle focus and blur events
                field.addEventListener('focus', (event) => handleElelemtActiveState(event));
                field.addEventListener('blur', (event) => handleElelemtActiveState(event));
                field.addEventListener('input', (event) => handleElelemtActiveState(event));

                // handle input events
                field.addEventListener('input', (event) => handleInputEvents(event));

                // handle next route update
                field.addEventListener('change', (event) => handleNextRouteUpdate(event, context));

                // handle checkbox field
                if (field.type === 'checkbox') {
                    field.addEventListener('change', (event) => handleCheckboxField(field));
                }
            });
        }
    }
}

/**
 * Function to handle input events
 *
 * @param {*} event
 * @returns
 */
export function handleInputEvents(event) {
    if (!event) return;

    const element = event.target; // may be text input, textarea or select
    const fieldType = element.type;
    const elementdValue = element.value ? element.value : '';

    const fieldData = {
        value: elementdValue,
        required: element.hasAttribute('required')
    };

    if (fieldType) {
        switch (fieldType) {
            case 'text':
            case 'textarea':
            case 'email':
            case 'number':
            case 'search':
            case 'password':
                // handle input required validation
                if (fieldData.required && !fieldData.value) {
                    validateRequiredField(element);
                }

                // handle email validation
                else if (fieldType === 'email' || element.hasAttribute('data-email-field')) {
                    validateEmailField(element);
                }

                // handle phone validation
                else if (
                    element.hasAttribute('data-phone-field') ||
                    element.name === 'phone_number'
                ) {
                    validatePhoneField(element);
                }

                // remove error message if valid
                else {
                    removeFieldError(element);
                }
                break;
            case 'select-one':
            case 'select-multiple':
                validateRequiredField(element);
                break;
            case 'checkbox':
                // Handle checkbox validation on change
                handleCheckboxField(element);
                break;
            default:
                break;
        }
    }
}

/**
* Ensures that stored values are correctly typed.

*/
export function parseFormData(data) {
    if (typeof data !== 'object' || data === null) return data;

    return Object.keys(data).reduce((acc, key) => {
        let value = data[key];

        if (typeof value === 'object' && value !== null) {
            acc[key] = parseFormData(value); // Recursively parse nested objects
        } else if (value === 'true') {
            acc[key] = true;
        } else if (value === 'false') {
            acc[key] = false;
        } else if (value === 'null' || value === null) {
            acc[key] = null; // Ensure null values are preserved
        } else if (value !== '' && !isNaN(value) && typeof value !== 'boolean') {
            acc[key] = Number(value); // Convert numbers, but ignore booleans
        } else {
            acc[key] = value;
        }

        return acc;
    }, {});
}

/**
 * Function to create computed properties for a store
 *
 * @param {*} store
 * @param {*} mappings
 * @returns
 */
export function createStoreComputed(store, mappings) {
    const computedProperties = {};

    // Custom formatting and unformatting functions
    const formatPhoneNumber = (value) =>
        value || value === 0 ? String(value).replace(/(\d{3})(\d{3})(\d{4})/, '$1.$2.$3') : '';
    const unformatPhoneNumber = (value) => String(value).replace(/\D/g, '');

    const formatSSN = (value) =>
        value || value === 0 ? String(value).replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3') : '';
    const unformatSSN = (value) => value.replace(/\D/g, '');

    const formatNumber = (value) => {
        if (value === '' || value === null || value === undefined) {
            return '';
        }

        if (value === 0) {
            return '0';
        }

        // Convert to string and remove any existing commas
        const cleanValue = String(value).replace(/,/g, '');

        // Check if the cleaned value is a valid number
        const numericValue = Number(cleanValue);
        if (isNaN(numericValue)) {
            // If invalid, return the original value to allow user correction
            return String(value);
        }

        return numericValue.toLocaleString();
    };

    const unformatNumber = (value) => {
        if (value === 0) {
            return 0;
        }

        if (typeof value !== 'string') {
            value = String(value || '');
        }

        // Remove commas and any non-numeric characters except decimal point
        const cleanValue = value.replace(/[^0-9.]/g, '');

        // Return the cleaned value as string to preserve leading zeros if any
        return cleanValue;
    };

    Object.entries(mappings).forEach(([computedName, config]) => {
        let storePath, format, unformat, mask;

        if (typeof config === 'string') {
            storePath = config;
            format = (value) => value;
            unformat = (value) => value;
            mask = null;
        } else if (typeof config === 'object' && config.path) {
            storePath = config.path;
            mask = config.mask || null;

            // Apply formatting functions based on the mask type
            if (mask === 'phone') {
                format = formatPhoneNumber;
                unformat = unformatPhoneNumber;
            } else if (mask === 'ssn') {
                format = formatSSN;
                unformat = unformatSSN;
            } else if (mask === 'number') {
                format = formatNumber;
                unformat = unformatNumber;
            } else {
                format = (value) => value;
                unformat = (value) => value;
            }
        } else {
            console.warn(`Invalid mapping for computed property: ${computedName}`);
            return;
        }

        computedProperties[computedName] = {
            get() {
                const value = getFromStore(storePath);
                return format(value);
            },
            set(value) {
                updateStore(storePath, unformat(value));
            }
        };
    });

    // Ensure the 'executionStatus' property is reactive
    if (!computedProperties['executionStatus']) {
        computedProperties['executionStatus'] = {
            get() {
                return store.execution_status;
            },
            set(value) {
                store.execution_status = value;
            }
        };
    }

    // Ensure 'application' is reactive
    if (!computedProperties['application']) {
        computedProperties['application'] = {
            get() {
                return store.application;
            },
            set(value) {
                store.application = value;
            }
        };
    }

    return computedProperties;
}

/**
 * Helper function to get a nested property
 *
 * @param {*} obj
 * @param {*} path
 * @returns
 */
function getNestedProperty(obj, path) {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

/**
 * Helper function to set a nested property
 *
 * @param {*} obj
 * @param {*} path
 * @param {*} value
 */
function setNestedProperty(obj, path, value) {
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
            current[keys[i]] = {};
        }
        current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
}

/**
 * Function to get contract type ID
 *
 * @param {*} workflowID
 * @returns
 */
export function getContractTypeID(workflowID) {
    workflowID = parseInt(workflowID);

    // if workflowID is less than or equal to 3, return 1, else return 2
    return workflowID <= 3 ? 1 : 2;
}

/**
 * Function to set contract type ID
 *
 * @param {*} contractID
 */
export function setContractTypeID(contractID) {
    contractID = parseInt(contractID);

    if (contractID) {
        updateStore('contract_type_id', contractID);
    }
}

/**
 * Function to update store
 *
 * @param {*} name
 * @param {*} value
 */
export function getFromStore(name) {
    if (!name) return;

    const store = useApplicationStore();

    // If no dot in name, return directly
    if (!name.includes('.')) {
        return store.application[name]; // Direct access for non-nested properties
    }

    return getNestedProperty(store.application, name);
}

/**
 * Function to update store
 *
 * @param {*} name
 * @param {*} value
 */
export function updateStore(name, value) {
    if (!name) return;

    const store = useApplicationStore();
    // If no dot in name, update directly
    if (!name.includes('.')) {
        store.application[name] = value;
        return;
    }

    // Otherwise, handle nested property update
    setNestedProperty(store.application, name, value);
}

/**
 * Function to save referral params
 * save (referral | preferred_ae_id_c) get params if they exist
 *
 */
export function saveReferralParams() {
    const route = useRoute();

    // Extract query params
    const { referral, preferred_ae_id_c } = route.query;

    // Save to store if they exist
    if (referral) {
        updateStore('referral', referral);
    }
    if (preferred_ae_id_c) {
        updateStore('preferred_ae_id_c', preferred_ae_id_c);
    }
}

/**
 * Function to set workflow step
 *
 * @param workflowStep
 */
export function setWorkflowStep(workflowStep) {
    workflowStep = workflowStep || null;

    if (workflowStep) {
        updateStore('workflow_step', workflowStep);
    } else {
        const step = findStepByRoute(useRoute().path);

        if (step && step.step) {
            updateStore('workflow_step', step.step);
        }
    }
}

/**
 * Function to set workflow
 *
 * @param workflow
 */
export function setWorkflow(workflow) {
    const store = useApplicationStore();

    updateStore('workflow', workflow);

    // Ensure Buy & Sell flow defaults to Own for ownership question
    if (parseInt(workflow, 10) === 2) {
        updateStore('own_or_rent', 'Own');
    }
}

/**
 * Function to set workflow step
 *
 * @param {*} route
 */
export function setWorkflowStep1(route) {
    const step = findStepByRoute(route);
    if (step && step.step) {
        updateStore('workflow_step', step.step);
    }
    // useApplicationStore().application.workflow_step = step;
}

let globalErrorTimeout = null;

/**
 * Function to show global error (context with errorMessage property is required or a default message is shown)
 *
 * @param {*} context
 * @returns
 */
export function showGlobalError(context) {
    if (!context) return;
    const msg =
        context && context.errorMessage.message
            ? context.errorMessage.message
            : 'An error occurred. Please try again.';
    const globalErrorElement = document.querySelector('.global-error');
    const globalErrorMsg = globalErrorElement
        ? globalErrorElement.querySelector('.error-message')
        : null;

    if (globalErrorMsg) {
        globalErrorMsg.textContent = msg;
        globalErrorElement.classList.remove('hide');

        // scroll into view
        globalErrorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });

        // Clear previous timeout
        if (globalErrorTimeout) {
            clearTimeout(globalErrorTimeout);
        }

        globalErrorTimeout = setTimeout(() => {
            globalErrorElement.classList.add('hide');
            globalErrorTimeout = null;
        }, 5000);
    }
}

/**
 * Function to show message
 *
 * @param {*} message
 * @param {*} type
 */
export function showMessage(message, type = 'error') {
    const messageElement = document.querySelector('.global-error');
    const messageText = messageElement ? messageElement.querySelector('.error-message') : null;

    if (messageText) {
        messageText.textContent = message;
        messageElement.classList.remove('hide');

        // scroll into view
        messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });
    }
}

export function focusErrorInfo() {
    setTimeout(() => {
        const errorElement = document.querySelector('.global-error');

        errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });
    }, 100);
}
/**
 * Function to handle continue button click
 *
 * @param {*} instance
 * @param {*} event
 * @param {*} syncData
 * @param {*} continueHandler
 */
export async function handleContinueHelper(instance, event, syncData, continueHandler = null) {
    // validate the form first
    validatePQForm(instance);

    // check if the form is valid
    const isValidForm = isValidPQForm();

    // if there are validation errors, stop here
    if (!isValidForm) {
        return;
    } else {
        const continueButton = document.querySelector('[data-next-btn]');

        if (continueButton) {
            continueButton.setAttribute('disabled', 'disabled');
        }

        try {
            const response = await syncData();

            if (response && response.status === 'success') {
                // if on phone verification page, don't allow user to go forward if he hasn't verified his phone
                const status = response.data?.phone_verification_status;
                if (!status || status.length === 0) {
                    continueHandler(instance, event);
                }

                if (continueButton) {
                    continueButton.removeAttribute('disabled');
                }

                return 'success';
            } else if (response.data?.message === 'recaptcha_invalid') {
                if (continueButton) {
                    continueButton.removeAttribute('disabled');
                }

                return 'recaptcha_invalid';
            } else if (response.data?.message === 'email_recognized') {
                if (continueButton) {
                    continueButton.removeAttribute('disabled');
                }

                redirectRecognizedUser(response);
            } else if (response.data?.message === 'update_not_allowed') {
                // enable the continue button
                if (continueButton) {
                    continueButton.removeAttribute('disabled');
                }

                instance.errorMessage = {
                    message: 'You cannot make changes to a completed application.'
                };

                setTimeout(() => {
                    const errorElement = document.querySelector('.global-error');

                    errorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }, 100);
            } else {
                if (continueButton) {
                    continueButton.removeAttribute('disabled');
                }

                let errorsString = '';

                if (response.data.errors) {
                    for (const key in response.data.errors) {
                        if (response.data.errors.hasOwnProperty(key)) {
                            errorsString += response.data.errors[key][0] + '<br>';
                        }
                    }
                }

                instance.errorMessage = {
                    message: getErrorMessage(response.status, errorsString)
                };

                setTimeout(() => {
                    const errorElement = document.querySelector('.global-error');

                    errorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }, 100);
            }
        } catch (error) {
            console.error(error);
            instance.errorMessage = { message: 'Unknown error occurred. Please try again later.' };
        }
    }
}

/**
 * Function to get error message
 *
 * @param {*} status
 * @param {*} customMessage
 * @returns
 */
export function getErrorMessage(status, customMessage = '') {
    const errorMessages = {
        400: customMessage || 'Bad Request.',
        422: customMessage || 'Validation error occurred. Please check your data and try again.',
        500: 'Server error occurred. Please try again later.',
        403: 'Access denied. Please contact support for assistance.'
    };

    return errorMessages[status] || 'Unknown error occurred. Please try again later.';
}

/**
 * Function to get Account Executive full name
 *
 * @param {*} id
 * @returns
 */
export async function getAEFullName(id) {
    try {
        const response = await axios.get('/api/account-executives');
        if (response.data.data) {
            const executive = response.data.data.find((ae) => ae.id === id);
            return executive ? executive.name : null;
        }
        return null;
    } catch (error) {
        console.error('Error fetching Account Executive:', error);
        return null;
    }
}

export async function fetchAccountExecutives() {
    try {
        const response = await axios.get('/api/account-executives');

        if (response.data.status === 'success' && response.data.data) {
            return response.data.data
                .map((executive) => ({
                    id: executive.id,
                    name: executive.name
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
        } else {
            console.error('Invalid response format:', response.data);
            return [];
        }
    } catch (error) {
        console.error('Error fetching Account Executives:', error);
        return [];
    }
}

export async function getUSStates() {
    try {
        const response = await axios.get('/api/states');

        if (response.data.status === 'success' && response.data.data) {
            return response.data.data
                .map((state) => ({
                    abbr: state.abbr,
                    state: state.state
                }))
                .sort((a, b) => a.abbr.localeCompare(b.abbr));
        } else {
            console.error('Invalid response format:', response.data);
            return [];
        }
    } catch (error) {
        console.error('Error fetching States:', error);
        return [];
    }
}

export function getUSStatesSell() {
    return [
        { abbr: 'AL', state: 'Alabama' },
        { abbr: 'AK', state: 'Alaska' },
        { abbr: 'AZ', state: 'Arizona' },
        { abbr: 'AR', state: 'Arkansas' },
        { abbr: 'CA', state: 'California' },
        { abbr: 'CO', state: 'Colorado' },
        { abbr: 'CT', state: 'Connecticut' },
        { abbr: 'DE', state: 'Delaware' },
        { abbr: 'FL', state: 'Florida' },
        { abbr: 'GA', state: 'Georgia' },
        { abbr: 'HI', state: 'Hawaii' },
        { abbr: 'ID', state: 'Idaho' },
        { abbr: 'IL', state: 'Illinois' },
        { abbr: 'IN', state: 'Indiana' },
        { abbr: 'IA', state: 'Iowa' },
        { abbr: 'KS', state: 'Kansas' },
        { abbr: 'KY', state: 'Kentucky' },
        { abbr: 'LA', state: 'Louisiana' },
        { abbr: 'ME', state: 'Maine' },
        { abbr: 'MD', state: 'Maryland' },
        { abbr: 'MA', state: 'Massachusetts' },
        { abbr: 'MI', state: 'Michigan' },
        { abbr: 'MN', state: 'Minnesota' },
        { abbr: 'MS', state: 'Mississippi' },
        { abbr: 'MO', state: 'Missouri' },
        { abbr: 'MT', state: 'Montana' },
        { abbr: 'NE', state: 'Nebraska' },
        { abbr: 'NV', state: 'Nevada' },
        { abbr: 'NH', state: 'New Hampshire' },
        { abbr: 'NJ', state: 'New Jersey' },
        { abbr: 'NM', state: 'New Mexico' },
        { abbr: 'NY', state: 'New York' },
        { abbr: 'NC', state: 'North Carolina' },
        { abbr: 'ND', state: 'North Dakota' },
        { abbr: 'OH', state: 'Ohio' },
        { abbr: 'OK', state: 'Oklahoma' },
        { abbr: 'OR', state: 'Oregon' },
        { abbr: 'PA', state: 'Pennsylvania' },
        { abbr: 'RI', state: 'Rhode Island' },
        { abbr: 'SC', state: 'South Carolina' },
        { abbr: 'SD', state: 'South Dakota' },
        { abbr: 'TN', state: 'Tennessee' },
        { abbr: 'TX', state: 'Texas' },
        { abbr: 'UT', state: 'Utah' },
        { abbr: 'VT', state: 'Vermont' },
        { abbr: 'VA', state: 'Virginia' },
        { abbr: 'WA', state: 'Washington' },
        { abbr: 'WV', state: 'West Virginia' },
        { abbr: 'WI', state: 'Wisconsin' },
        { abbr: 'WY', state: 'Wyoming' }
    ];
}

/**
 * Function to check if a state is allowed
 *
 * @param {*} stateAbbr
 * @returns
 */
export function isStateNotAllowed(stateAbbr) {
    stateAbbr = stateAbbr || '';
    const allowedStates = [
        'AL', // Alabama
        'AZ', // Arizona
        'AR', // Arkansas
        'CA', // California
        'CO', // Colorado
        'CT', // Connecticut
        'DE', // Delaware
        'DC', // District of Columbia
        'FL', // Florida
        'GA', // Georgia
        'IA', // Iowa
        'IL', // Illinois
        'IN', // Indiana
        'KS', // Kansas
        'KY', // Kentucky
        'MD', // Maryland
        'MA', // Massachusetts
        'MI', // Michigan
        'MN', // Minnesota
        'MS', // Mississippi
        'MO', // Missouri
        'NJ', // New Jersey
        'NY', // New York
        'NC', // North Carolina
        'OH', // Ohio
        'OR', // Oregon
        'PA', // Pennsylvania
        'RI', // Rhode Island
        'SC', // South Carolina
        'TN', // Tennessee
        'TX', // Texas
        'VA', // Virginia
        'WA', // Washington
        'WV', // West Virginia
        'WI' // Wisconsin
    ];

    return !allowedStates.includes(stateAbbr.toUpperCase());
}

/**
 * Function to detect if the page has a scrollbar
 * Only runs on pages that contain maps (element with id "map")
 * Waits for the map to be loaded/mounted before starting detection
 * Cleans up CSS classes when map is removed from the page
 *
 * @returns
 */
export function useScrollbarDetection() {
    let resizeListener = null;
    let observer = null;
    let checkInterval = null;
    let mapDetectionActive = false;

    const cleanupClasses = () => {
        document.body.classList.remove('has-scrollbar');
        document.body.classList.remove('has-no-scrollbar');
        document.documentElement.classList.remove('has-scrollbar');
        document.documentElement.classList.remove('has-no-scrollbar');
    };

    const checkScrollbar = () => {
        // Check if map still exists before applying scrollbar detection
        const mapElement = document.getElementById('map');
        if (!mapElement) {
            cleanupClasses();
            stopScrollbarDetection();
            startMapMonitoring();
            return;
        }

        // More reliable method to check for scrollbars
        const windowHeight = window.innerHeight;
        const documentHeight = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
        );

        // Check if scrollbar is actually visible
        const hasScroll = documentHeight > windowHeight;

        // Add appropriate classes based on scrollbar presence
        document.body.classList.toggle('has-scrollbar', hasScroll);
        document.body.classList.toggle('has-no-scrollbar', !hasScroll);
        document.documentElement.classList.toggle('has-scrollbar', hasScroll);
        document.documentElement.classList.toggle('has-no-scrollbar', !hasScroll);
    };

    const stopScrollbarDetection = () => {
        if (resizeListener) {
            window.removeEventListener('resize', resizeListener);
            resizeListener = null;
        }
        mapDetectionActive = false;
    };

    const initScrollbarDetection = () => {
        // Stop any existing detection first
        stopScrollbarDetection();

        // Start scrollbar detection
        mapDetectionActive = true;
        resizeListener = checkScrollbar;
        window.addEventListener('resize', resizeListener);

        // Initial check
        checkScrollbar();

        // Delayed second check (for content that loads after initial render)
        setTimeout(checkScrollbar, 1500);
    };

    const startMapMonitoring = () => {
        if (checkInterval) return; // Already monitoring

        // Fallback: Check for map element periodically
        checkInterval = setInterval(() => {
            const mapElement = document.getElementById('map');
            if (mapElement && !mapDetectionActive) {
                clearInterval(checkInterval);
                checkInterval = null;
                initScrollbarDetection();
            }
        }, 100);
    };

    // Use MutationObserver to watch for map element being added/removed from DOM
    if (typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    const mapElement = document.getElementById('map');

                    if (mapElement && !mapDetectionActive) {
                        // Map found and detection not active - start detection
                        if (checkInterval) {
                            clearInterval(checkInterval);
                            checkInterval = null;
                        }
                        initScrollbarDetection();
                    } else if (!mapElement && mapDetectionActive) {
                        // Map removed and detection is active - stop detection and cleanup
                        cleanupClasses();
                        stopScrollbarDetection();
                        startMapMonitoring();
                    }
                }
            });
        });

        // Start observing
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    // Initial check and start monitoring
    const mapElement = document.getElementById('map');
    if (mapElement) {
        initScrollbarDetection();
    } else {
        startMapMonitoring();
    }

    // Return cleanup function
    return () => {
        stopScrollbarDetection();
        if (observer) {
            observer.disconnect();
        }
        if (checkInterval) {
            clearInterval(checkInterval);
        }
        cleanupClasses();
    };
}
